import { defineMessages } from '@edx/frontend-platform/i18n';

const messages = defineMessages({
  navHeading: {
    id: 'portal.views.cohortDetail.nav.heading',
    defaultMessage: 'Navigation',
    description: 'Heading above the cohort sidebar navigation list',
  },
  navMembers: {
    id: 'portal.views.cohortDetail.nav.members',
    defaultMessage: 'Members',
    description: 'Sidebar navigation item linking to the cohort members view',
  },
  navInfo: {
    id: 'portal.views.cohortDetail.nav.info',
    defaultMessage: 'Cohort Info',
    description: 'Sidebar navigation item linking to the cohort info view',
  },
  navDiscussions: {
    id: 'portal.views.cohortDetail.nav.discussions',
    defaultMessage: 'Discussions',
    description: 'Sidebar navigation item linking to the cohort discussions moderation view',
  },
  sidebarMemberStatus: {
    id: 'portal.views.cohortDetail.sidebar.memberStatus',
    defaultMessage: '{count, plural, one {# member} other {# members}} · Active',
    description: 'Member count and active status shown under the cohort name in the sidebar',
  },
  breadcrumbMyCohorts: {
    id: 'portal.views.cohortDetail.breadcrumb.myCohorts',
    defaultMessage: 'My Cohorts',
    description: 'Breadcrumb link back to the My Cohorts page',
  },
  breadcrumbAriaLabel: {
    id: 'portal.views.cohortDetail.breadcrumb.ariaLabel',
    defaultMessage: 'Breadcrumb',
    description: 'Accessible label for the cohort breadcrumb navigation',
  },
  detailLoadError: {
    id: 'portal.views.cohortDetail.loadError',
    defaultMessage: 'We couldn’t load this cohort. Please try again.',
    description: 'Error state shown when the cohort detail request fails (server error or network failure)',
  },
  roleCoFacilitator: {
    id: 'portal.views.cohortDetail.role.coFacilitator',
    defaultMessage: 'Co-Facilitator',
    description: 'Cohort membership role: co-facilitator',
  },
  loadMoreButton: {
    id: 'portal.views.cohortDetail.loadMore',
    defaultMessage: 'Load more',
    description: 'Button that fetches the next page of members',
  },
  discussionsModuleSelectLabel: {
    id: 'portal.views.cohortDetail.discussions.moduleSelectLabel',
    defaultMessage: 'Module',
    description: 'Label for the module selector on the Discussions tab',
  },
  discussionsNoCoursesMessage: {
    id: 'portal.views.cohortDetail.discussions.noCourses',
    defaultMessage: 'No courses are available yet for this cohort’s language.',
    description: 'Shown on the Discussions tab when the cohort’s language path has no courses configured',
  },
  discussionsThreadsLoading: {
    id: 'portal.views.cohortDetail.discussions.loading',
    defaultMessage: 'Loading discussions…',
    description: 'Loading state for the thread list on the Discussions tab',
  },
  discussionsThreadsLoadError: {
    id: 'portal.views.cohortDetail.discussions.loadError',
    defaultMessage: 'We couldn’t load this course’s discussions. Please try again.',
    description: 'Error state when fetching the thread list fails',
  },
  discussionsThreadsEmpty: {
    id: 'portal.views.cohortDetail.discussions.empty',
    defaultMessage: 'No discussion posts yet for this course.',
    description: 'Empty state when the selected course has no forum threads',
  },
  discussionsThreadAuthorLabel: {
    id: 'portal.views.cohortDetail.discussions.threadAuthor',
    defaultMessage: 'By {authorName}',
    description: 'Author attribution on a thread row',
  },
  discussionsUnknownAuthor: {
    id: 'portal.views.cohortDetail.discussions.unknownAuthor',
    defaultMessage: 'A learner',
    description: 'Fallback author label when the thread author’s display name could not be resolved',
  },
  discussionsThreadReplyCountLabel: {
    id: 'portal.views.cohortDetail.discussions.replyCount',
    defaultMessage: '{count} {count, plural, one {reply} other {replies}}',
    description: 'Reply count on a thread row',
  },
  discussionsDeleteButton: {
    id: 'portal.views.cohortDetail.discussions.deleteButton',
    defaultMessage: 'Delete',
    description: 'Button on a thread row that opens the delete-confirmation modal',
  },
  discussionsDeleteConfirmTitle: {
    id: 'portal.views.cohortDetail.discussions.deleteConfirmTitle',
    defaultMessage: 'Delete this post?',
    description: 'Title of the thread delete-confirmation modal',
  },
  discussionsDeleteConfirmBody: {
    id: 'portal.views.cohortDetail.discussions.deleteConfirmBody',
    defaultMessage: 'This will permanently delete the post and all its replies. This action cannot be undone.',
    description: 'Body copy of the thread delete-confirmation modal',
  },
  discussionsDeleteCancelButton: {
    id: 'portal.views.cohortDetail.discussions.deleteCancelButton',
    defaultMessage: 'Cancel',
    description: 'Cancel button on the thread delete-confirmation modal',
  },
  discussionsDeleteConfirmButton: {
    id: 'portal.views.cohortDetail.discussions.deleteConfirmButton',
    defaultMessage: 'Delete',
    description: 'Confirm button on the thread delete-confirmation modal',
  },
  discussionsDeleteSuccessToast: {
    id: 'portal.views.cohortDetail.discussions.deleteSuccessToast',
    defaultMessage: 'Post deleted.',
    description: 'Toast shown after a thread is successfully deleted',
  },
  discussionsDeleteErrorToast: {
    id: 'portal.views.cohortDetail.discussions.deleteErrorToast',
    defaultMessage: 'We couldn’t delete this post. Please try again.',
    description: 'Toast shown when deleting a thread fails (404 or 403)',
  },
  searchPlaceholder: {
    id: 'portal.views.cohortDetail.search.placeholder',
    defaultMessage: 'Search members…',
    description: 'Placeholder for the members search field',
  },
  memberJoinedLabel: {
    id: 'portal.views.cohortDetail.member.joined',
    defaultMessage: 'Joined {date}',
    description: 'The date a member joined the cohort, shown on a member card',
  },
  badgeFacilitator: {
    id: 'portal.views.cohortDetail.badge.facilitator',
    defaultMessage: 'Facilitator',
    description: 'Badge on the owner (facilitator) member card',
  },
  progressModulesCount: {
    id: 'portal.views.cohortDetail.progress.modulesCount',
    defaultMessage: '{completed} / {total} {total, plural, one {module} other {modules}}',
    description: 'Aggregate completed-vs-total module count on a member card',
  },
  progressTitle: {
    id: 'portal.views.cohortDetail.progress.title',
    defaultMessage: 'Module Progress',
    description: 'Heading of the expandable per-module progress panel on a member card',
  },
  progressCompleted: {
    id: 'portal.views.cohortDetail.progress.completed',
    defaultMessage: 'Completed',
    description: 'Accessible label for a completed module chip',
  },
  progressNotCompleted: {
    id: 'portal.views.cohortDetail.progress.notCompleted',
    defaultMessage: 'Not completed',
    description: 'Accessible label for a not-yet-completed module chip',
  },
  progressToggleExpand: {
    id: 'portal.views.cohortDetail.progress.expand',
    defaultMessage: 'Show module progress',
    description: 'Accessible label for the chevron that expands the module progress panel',
  },
  progressToggleCollapse: {
    id: 'portal.views.cohortDetail.progress.collapse',
    defaultMessage: 'Hide module progress',
    description: 'Accessible label for the chevron that collapses the module progress panel',
  },
  membersLoading: {
    id: 'portal.views.cohortDetail.members.loading',
    defaultMessage: 'Loading members…',
    description: 'Loading state for the members list',
  },
  membersLoadError: {
    id: 'portal.views.cohortDetail.members.loadError',
    defaultMessage: 'We couldn’t load members. Please try again.',
    description: 'Error state when the members progress request fails',
  },
  membersRetry: {
    id: 'portal.views.cohortDetail.members.retry',
    defaultMessage: 'Try again',
    description: 'Button that re-requests the members list after a failed load',
  },
  membersEmpty: {
    id: 'portal.views.cohortDetail.members.empty',
    defaultMessage: 'No members yet.',
    description: 'Empty state when the cohort has no members',
  },
  membersSearchEmpty: {
    id: 'portal.views.cohortDetail.members.searchEmpty',
    defaultMessage: 'No members match your search.',
    description: 'Empty state when a member search returns no results',
  },
  pagerPrevious: {
    id: 'portal.views.cohortDetail.pager.previous',
    defaultMessage: 'Previous page',
    description: 'Accessible label for the previous-page pager arrow',
  },
  pagerNext: {
    id: 'portal.views.cohortDetail.pager.next',
    defaultMessage: 'Next page',
    description: 'Accessible label for the next-page pager arrow',
  },
  pagerStatus: {
    id: 'portal.views.cohortDetail.pager.status',
    defaultMessage: 'Page {page} of {total}',
    description: 'Current page indicator between the pager arrows',
  },
  infoLabelName: {
    id: 'portal.views.cohortDetail.info.labelName',
    defaultMessage: 'Cohort Name',
    description: 'Label for the cohort name field on the Cohort Info card',
  },
  infoLabelDescription: {
    id: 'portal.views.cohortDetail.info.labelDescription',
    defaultMessage: 'Description',
    description: 'Label for the description field on the Cohort Info card',
  },
  infoNoDescription: {
    id: 'portal.views.cohortDetail.info.noDescription',
    defaultMessage: 'No description yet.',
    description: 'Placeholder shown on the Cohort Info card when the cohort has no description',
  },
  infoLabelCourse: {
    id: 'portal.views.cohortDetail.info.labelCourse',
    defaultMessage: 'Course',
    description: 'Label for the course/program field on the Cohort Info card',
  },
  infoProgramName: {
    id: 'portal.views.cohortDetail.info.programName',
    defaultMessage: 'Leading with Character',
    description: 'Static program title shown as the Course value on the Cohort Info card',
  },
  infoLabelFacilitator: {
    id: 'portal.views.cohortDetail.info.labelFacilitator',
    defaultMessage: 'Facilitator',
    description: 'Label for the facilitator (cohort owner) field on the Cohort Info card',
  },
  infoLabelMembers: {
    id: 'portal.views.cohortDetail.info.labelMembers',
    defaultMessage: 'Members',
    description: 'Label for the member count field on the Cohort Info card',
  },
  infoMembersValue: {
    id: 'portal.views.cohortDetail.info.membersValue',
    defaultMessage: '{count, plural, one {# learner} other {# learners}}',
    description: 'Member count value on the Cohort Info card',
  },
  infoLabelCreated: {
    id: 'portal.views.cohortDetail.info.labelCreated',
    defaultMessage: 'Created',
    description: 'Label for the created-date field on the Cohort Info card',
  },
  infoEdit: {
    id: 'portal.views.cohortDetail.info.edit',
    defaultMessage: 'Edit',
    description: 'Button (owner/co-facilitator only) that switches the Cohort Info card into edit mode',
  },
  infoEditTitle: {
    id: 'portal.views.cohortDetail.info.editTitle',
    defaultMessage: 'Edit cohort details',
    description: 'Heading shown at the top of the Cohort Info card while editing name/description',
  },
  infoNameRequired: {
    id: 'portal.views.cohortDetail.info.field.name.required',
    defaultMessage: 'Cohort name is required.',
    description: 'Validation error when the cohort name is left blank while editing',
  },
  infoNameTooLong: {
    id: 'portal.views.cohortDetail.info.field.name.tooLong',
    defaultMessage: 'Cohort name must be 255 characters or fewer.',
    description: 'Validation error when the edited cohort name exceeds 255 characters',
  },
  infoDescriptionTooLong: {
    id: 'portal.views.cohortDetail.info.field.description.tooLong',
    defaultMessage: 'Description must be 2000 characters or fewer.',
    description: 'Validation error when the edited cohort description exceeds 2000 characters',
  },
  infoSave: {
    id: 'portal.views.cohortDetail.info.save',
    defaultMessage: 'Save',
    description: 'Button that saves the edited cohort name/description',
  },
  infoCancel: {
    id: 'portal.views.cohortDetail.info.cancel',
    defaultMessage: 'Cancel',
    description: 'Button that discards the cohort edit and restores the displayed values',
  },
  infoToastSaved: {
    id: 'portal.views.cohortDetail.info.toast.saved',
    defaultMessage: 'Cohort details updated.',
    description: 'Success toast shown after the cohort name/description is saved',
  },
  infoToastError: {
    id: 'portal.views.cohortDetail.info.toast.error',
    defaultMessage: 'We couldn’t save your changes. Please try again.',
    description: 'Error toast shown when saving the cohort name/description fails',
  },
  infoLabelUrl: {
    id: 'portal.views.cohortDetail.info.label.url',
    defaultMessage: 'Cohort URL',
    description: 'Label of the editable cohort invite-URL (slug) field on the Cohort Info card',
  },
  infoUrlHint: {
    id: 'portal.views.cohortDetail.info.url.hint',
    defaultMessage: 'This is the last part of the invite link people use to join.',
    description: 'Helper text under the editable cohort URL field explaining what the slug is',
  },
  infoUrlChangeWarning: {
    id: 'portal.views.cohortDetail.info.url.changeWarning',
    defaultMessage: 'Heads up: changing the URL breaks every invite link you have already shared. Anyone using an old link will see “cohort not found”.',
    description: 'Warning shown before saving when the facilitator has changed the cohort URL (slug)',
  },
  infoUrlRequired: {
    id: 'portal.views.cohortDetail.info.url.required',
    defaultMessage: 'Cohort URL is required.',
    description: 'Validation error when the cohort URL field is left empty',
  },
  infoUrlInvalid: {
    id: 'portal.views.cohortDetail.info.url.invalid',
    defaultMessage: 'Use letters, numbers and hyphens — this URL has no usable characters.',
    description: 'Validation error when the cohort URL contains no characters usable in a slug (400 invalid_slug)',
  },
  infoUrlTaken: {
    id: 'portal.views.cohortDetail.info.url.taken',
    defaultMessage: 'That URL is already in use. Please choose a different one.',
    description: 'Error message when the requested cohort URL is already taken (409 slug_taken)',
  },
  infoUrlForbidden: {
    id: 'portal.views.cohortDetail.info.url.forbidden',
    defaultMessage: 'Only the cohort owner can change the URL.',
    description: 'Error message when a co-facilitator tries to change the cohort URL (403 owner_role_required_for_slug)',
  },
  infoToastUrlSaved: {
    id: 'portal.views.cohortDetail.info.toast.urlSaved',
    defaultMessage: 'Cohort URL updated. New invite link: {link}',
    description: 'Success toast after a cohort URL change, showing the new invite link',
  },
  inviteLabel: {
    id: 'portal.views.cohortDetail.invite.label',
    defaultMessage: 'Invite Link',
    description: 'Label for the invite-link box on the Cohort Info view',
  },
  inviteCopyButton: {
    id: 'portal.views.cohortDetail.invite.copyButton',
    defaultMessage: 'Copy Link',
    description: 'Button that copies the cohort invite link to the clipboard',
  },
  inviteCopiedToast: {
    id: 'portal.views.cohortDetail.invite.copiedToast',
    defaultMessage: 'Invite link copied to clipboard.',
    description: 'Toast shown after the cohort invite link is copied successfully',
  },
  inviteCopyErrorToast: {
    id: 'portal.views.cohortDetail.invite.copyErrorToast',
    defaultMessage: 'We couldn’t copy the link. Please copy it manually.',
    description: 'Toast shown when copying the cohort invite link to the clipboard fails',
  },
  memberActionsMenuLabel: {
    id: 'portal.views.cohortDetail.memberActions.menuLabel',
    defaultMessage: 'Member actions',
    description: 'Accessible label for the kebab (⋮) actions menu on a member card',
  },
  memberActionsMakeCoFacilitator: {
    id: 'portal.views.cohortDetail.memberActions.makeCoFacilitator',
    defaultMessage: 'Make co-facilitator',
    description: 'Menu item (owner only) that promotes a member to co-facilitator',
  },
  memberActionsRevokeCoFacilitator: {
    id: 'portal.views.cohortDetail.memberActions.revokeCoFacilitator',
    defaultMessage: 'Remove co-facilitator role',
    description: 'Menu item (owner only) that demotes a co-facilitator back to member',
  },
  memberActionsRemoveMember: {
    id: 'portal.views.cohortDetail.memberActions.removeMember',
    defaultMessage: 'Remove from cohort',
    description: 'Menu item that removes a member from the cohort',
  },
  memberActionsConfirmPromoteTitle: {
    id: 'portal.views.cohortDetail.memberActions.confirmPromoteTitle',
    defaultMessage: 'Make co-facilitator?',
    description: 'Title of the confirm dialog for promoting a member to co-facilitator',
  },
  memberActionsConfirmPromoteBody: {
    id: 'portal.views.cohortDetail.memberActions.confirmPromoteBody',
    defaultMessage: 'Give {name} co-facilitator access to this cohort? They will be able to view member progress, moderate discussions, and remove members.',
    description: 'Body of the confirm dialog for promoting a member; {name} is the member’s display name',
  },
  memberActionsConfirmPromoteConfirm: {
    id: 'portal.views.cohortDetail.memberActions.confirmPromoteConfirm',
    defaultMessage: 'Make co-facilitator',
    description: 'Confirm button of the promote-to-co-facilitator dialog',
  },
  memberActionsConfirmDemoteTitle: {
    id: 'portal.views.cohortDetail.memberActions.confirmDemoteTitle',
    defaultMessage: 'Remove co-facilitator role?',
    description: 'Title of the confirm dialog for revoking a co-facilitator role',
  },
  memberActionsConfirmDemoteBody: {
    id: 'portal.views.cohortDetail.memberActions.confirmDemoteBody',
    defaultMessage: 'Remove {name}’s co-facilitator access? They will stay a member of the cohort.',
    description: 'Body of the confirm dialog for revoking a co-facilitator role; {name} is the member’s display name',
  },
  memberActionsConfirmDemoteConfirm: {
    id: 'portal.views.cohortDetail.memberActions.confirmDemoteConfirm',
    defaultMessage: 'Remove role',
    description: 'Confirm button of the revoke-co-facilitator dialog',
  },
  memberActionsConfirmRemoveTitle: {
    id: 'portal.views.cohortDetail.memberActions.confirmRemoveTitle',
    defaultMessage: 'Remove from cohort?',
    description: 'Title of the confirm dialog for removing a member from the cohort',
  },
  memberActionsConfirmRemoveBody: {
    id: 'portal.views.cohortDetail.memberActions.confirmRemoveBody',
    defaultMessage: 'Remove {name} from this cohort? They will lose access to the cohort and its discussions. They can re-join later with the invite link.',
    description: 'Body of the confirm dialog for removing a member; {name} is the member’s display name',
  },
  memberActionsConfirmRemoveConfirm: {
    id: 'portal.views.cohortDetail.memberActions.confirmRemoveConfirm',
    defaultMessage: 'Remove',
    description: 'Confirm button of the remove-member dialog',
  },
  memberActionsConfirmCancel: {
    id: 'portal.views.cohortDetail.memberActions.confirmCancel',
    defaultMessage: 'Cancel',
    description: 'Cancel button shared by the member-action confirm dialogs',
  },
  memberActionsToastPromoted: {
    id: 'portal.views.cohortDetail.memberActions.toast.promoted',
    defaultMessage: '{name} is now a co-facilitator.',
    description: 'Success toast after promoting a member to co-facilitator',
  },
  memberActionsToastDemoted: {
    id: 'portal.views.cohortDetail.memberActions.toast.demoted',
    defaultMessage: '{name} is no longer a co-facilitator.',
    description: 'Success toast after revoking a co-facilitator role',
  },
  memberActionsToastRemoved: {
    id: 'portal.views.cohortDetail.memberActions.toast.removed',
    defaultMessage: '{name} was removed from the cohort.',
    description: 'Success toast after removing a member from the cohort',
  },
  memberActionsToastNotAllowed: {
    id: 'portal.views.cohortDetail.memberActions.toast.notAllowed',
    defaultMessage: 'You don’t have permission to do that.',
    description: 'Error toast shown when a member action is rejected with 403',
  },
  memberActionsToastAlreadyGone: {
    id: 'portal.views.cohortDetail.memberActions.toast.alreadyGone',
    defaultMessage: 'That member is no longer in this cohort.',
    description: 'Info toast shown when a member action targets someone already removed (404)',
  },
  memberActionsToastGenericError: {
    id: 'portal.views.cohortDetail.memberActions.toast.genericError',
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Generic error toast for a failed member action (409 or other)',
  },
});

export default messages;
